// @view/panels/workspace/AccountManagement.panel.tsx
import React from "react";
import { Building2, KeyRound, ShieldCheck, UserCircle2 } from "lucide-react";
import * as styles from "@styles/AccountManagement.css";
import "@styles/util/AccountManagement.global.css";
import EmptyView from "@view/EmptyView";
import type { IOts } from "@ps-bridge/interop";
import type { Ok } from "@ps-bridge/ok";

export type AccountManagementViewPhase = "loading" | "ready";
export type AccountManagementModalPhase = "closed" | "request" | "complete";

export type AccountManagementPanelProps = {
  viewPhase: AccountManagementViewPhase;
  modalPhase: AccountManagementModalPhase;
  fullNameText: string;
  emailText: string;
  subscriptionStatusText: string;
  organizationNameText: string;
  membershipRoleText: string;
  membershipStatusText: string;
  modalEmailText: string;
  passwordSubmitting: boolean;
  passwordErrorMessage: string;
  resetToken: string;
  newPassword: string;
  confirmPassword: string;
  onOpenPasswordModal: IOts<Ok>;
  onClosePasswordModal: IOts<Ok>;
  onSendReset: IOts<Ok>;
  onCompleteReset: IOts<Ok>;
  runIO: <A>(value: IOts<A>) => A;
  onResetTokenChange: (value: string) => Ok;
  onNewPasswordChange: (value: string) => Ok;
  onConfirmPasswordChange: (value: string) => Ok;
};

const AccountManagementPanel: React.FC<AccountManagementPanelProps> = ({
  viewPhase,
  modalPhase,
  fullNameText,
  emailText,
  subscriptionStatusText,
  organizationNameText,
  membershipRoleText,
  membershipStatusText,
  modalEmailText,
  passwordSubmitting,
  passwordErrorMessage,
  resetToken,
  newPassword,
  confirmPassword,
  onOpenPasswordModal,
  onClosePasswordModal,
  onSendReset,
  onCompleteReset,
  runIO,
  onResetTokenChange,
  onNewPasswordChange,
  onConfirmPasswordChange,
}) => {
  const modalBody = {
    request: (
      <div className={styles.modalBody}>
        <div className={styles.modalRow}>
          <span className={styles.modalLabel}>Email</span>
          <span className={styles.modalValue}>{modalEmailText}</span>
        </div>
      </div>
    ),
    complete: (
      <div className={styles.modalBody}>
        <label className={styles.modalField}>
          Verification code
          <input
            className={styles.modalInput}
            value={resetToken}
            onChange={(event) => onResetTokenChange(event.target.value)}
            placeholder="Paste the code from your email"
            data-testid="account-password-token-input"
          />
        </label>
        <label className={styles.modalField}>
          New password
          <input
            className={styles.modalInput}
            type="password"
            value={newPassword}
            onChange={(event) => onNewPasswordChange(event.target.value)}
            placeholder="Create a strong password"
            data-testid="account-password-new-input"
          />
        </label>
        <label className={styles.modalField}>
          Confirm new password
          <input
            className={styles.modalInput}
            type="password"
            value={confirmPassword}
            onChange={(event) => onConfirmPasswordChange(event.target.value)}
            placeholder="Repeat password"
            data-testid="account-password-confirm-input"
          />
        </label>
      </div>
    ),
    closed: <EmptyView />,
  } as const satisfies Record<AccountManagementModalPhase, React.ReactElement>;

  const errorPhase = passwordErrorMessage.length > 0 ? "visible" : "hidden";
  const errorView = {
    hidden: <EmptyView />,
    visible: (
      <div className={styles.modalError} data-testid="account-password-error">
        {passwordErrorMessage}
      </div>
    ),
  } as const satisfies Record<"hidden" | "visible", React.ReactElement>;

  const modalView = {
    closed: <EmptyView />,
    request: (
      <div
        className={styles.modalOverlay}
        role="dialog"
        aria-modal="true"
        data-testid="account-password-modal"
      >
        <div className={styles.modalCard}>
          <div className={styles.modalHeader}>
            <h3>Change password</h3>
            <p>We will send a verification code to your email.</p>
          </div>
          {modalBody.request}
          {errorView[errorPhase]}
          <div className={styles.modalActions}>
            <button
              className={styles.secondaryButton}
              onClick={() => runIO(onClosePasswordModal)}
              data-testid="account-password-cancel"
            >
              Cancel
            </button>
            <button
              className={styles.primaryButton}
              onClick={() => runIO(onSendReset)}
              disabled={passwordSubmitting}
              data-testid="account-password-send-code"
            >
              Send code
            </button>
          </div>
        </div>
      </div>
    ),
    complete: (
      <div
        className={styles.modalOverlay}
        role="dialog"
        aria-modal="true"
        data-testid="account-password-modal"
      >
        <div className={styles.modalCard}>
          <div className={styles.modalHeader}>
            <h3>Change password</h3>
            <p>Enter the code and your new password to finish.</p>
          </div>
          {modalBody.complete}
          {errorView[errorPhase]}
          <div className={styles.modalActions}>
            <button
              className={styles.secondaryButton}
              onClick={() => runIO(onClosePasswordModal)}
              data-testid="account-password-cancel"
            >
              Cancel
            </button>
            <button
              className={styles.primaryButton}
              onClick={() => runIO(onCompleteReset)}
              disabled={passwordSubmitting}
              data-testid="account-password-update"
            >
              Update password
            </button>
          </div>
        </div>
      </div>
    ),
  } as const satisfies Record<AccountManagementModalPhase, React.ReactElement>;

  const contentView = {
    loading: <p className={styles.loading}>Loading account details...</p>,
    ready: (
      <div className={styles.page}>
        <header className={styles.header}>
          <p className={styles.kicker}>Settings</p>
          <h1 className={styles.title}>User settings</h1>
          <p className={styles.subtitle}>
            Manage your profile, organization membership, and security preferences.
          </p>
        </header>

        <div className={styles.grid}>
          <section className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardIcon}>
                <UserCircle2 size={20} />
              </div>
              <div>
                <h2 className={styles.cardTitle}>Profile</h2>
                <p className={styles.cardSubtitle}>
                  Your identity and access details.
                </p>
              </div>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Name</span>
              <span className={styles.detailValue}>{fullNameText}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Email</span>
              <span className={styles.detailValue}>{emailText}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Subscription status</span>
              <span className={styles.detailValue}>{subscriptionStatusText}</span>
            </div>
          </section>

          <section className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardIcon}>
                <Building2 size={20} />
              </div>
              <div>
                <h2 className={styles.cardTitle}>Organization membership</h2>
                <p className={styles.cardSubtitle}>Workspace affiliation and role.</p>
              </div>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Organization</span>
              <span className={styles.detailValue}>{organizationNameText}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Role</span>
              <span className={styles.detailValue}>{membershipRoleText}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Status</span>
              <span className={styles.detailValue}>{membershipStatusText}</span>
            </div>
          </section>

          <section className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardIcon}>
                <ShieldCheck size={20} />
              </div>
              <div>
                <h2 className={styles.cardTitle}>Security</h2>
                <p className={styles.cardSubtitle}>
                  Update your password without leaving the workspace.
                </p>
              </div>
            </div>
            <button
              className={styles.primaryButton}
              onClick={() => runIO(onOpenPasswordModal)}
              data-testid="account-change-password"
            >
              <KeyRound size={16} />
              Change password
            </button>
          </section>
        </div>

        {modalView[modalPhase]}
      </div>
    ),
  } as const satisfies Record<AccountManagementViewPhase, React.ReactElement>;

  return (
    <div
      className={styles.container}
      data-testid="page-account"
      data-page-state={viewPhase === "ready" ? "ready" : "init"}
    >
      {contentView[viewPhase]}
    </div>
  );
};

export default AccountManagementPanel;
